import DlsLink from "@galaxy-io/dls/links/Link";
import Text, { TextSize, TextWeight } from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { ActivityItem } from "@gtm/contracts";
import { activityVerb, pageNumber } from "@gtm/contracts";
import { activitySortKeys } from "@gtm/contracts/core/sorting";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useUrlState } from "@/app/navigation";
import { FeedViewport, Inline } from "@/components/workspace/people-styles";
import { RecordTable, type TablePaging } from "@/components/workspace/record-table";
import { RelativeTime } from "@/components/workspace/relative-time";
import { SourceLabel } from "@/components/workspace/source-icon";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { useTableSort } from "@/components/workspace/use-table-sort";
import { ActivitySignalChip } from "@/features/activity/activity-signal-chip";
import { ActivityTimeline } from "@/features/activity/activity-timeline";
import { PersonLink } from "@/features/people/person-link";
import { Avatar } from "@/features/people/profile-drawer";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { toastDefaults } from "@/lib/toast";
import { date, safeUrl } from "@/lib/utils";

export function PeopleFeed({
  items,
  sort,
  showActions = true,
  paging,
  isLoading = false,
  error,
  onRetry,
  selectBatch,
}: {
  items: ActivityItem[];
  selectBatch?: () => Promise<Array<{ id: string; key: string }>>;
  sort?: import("@/components/workspace/use-table-sort").TableSort;
  isLoading?: boolean;
  error?: Error;
  onRetry?: () => void;
  paging?: TablePaging;
  showActions?: boolean;
}) {
  const { update } = useUrlState();
  const columns = [
    { key: "person", label: "Person" },
    { key: "signal", label: "Signal", width: 190 },
    { key: "when", label: "Date", width: 170 },
    { key: "source", label: "Source", width: 230 },
    { key: "activity", label: "Activity", width: "auto" as const },
  ];
  return (
    <RecordTable
      rowDetails
      selectBatch={selectBatch}
      sort={sort}
      isLoading={isLoading || sort?.pending}
      error={error}
      onRetry={onRetry}
      auditKind="activity"
      rowKeys={items.map((item) => item.id)}
      editableStatus={showActions}
      // A signal is something a person did, so opening one opens the person.
      // The signal chip opens the individual signal instead.
      onRowClick={(key) => {
        const personId = items.find((item) => item.id === key)?.person_id;
        if (!personId) return;
        update({
          personId,
          person: null,
          personTab: null,
          companyId: null,
          companyTab: null,
          companyPage: null,
          activityId: null,
          runId: null,
        });
      }}
      paging={paging}
      label="People activity"
      columns={columns}
      reviewKeys={showActions ? items.map((s) => s.id) : undefined}
      details={[
        ...columns,
        { key: "observed", label: "Discovered" },
        { key: "description", label: "Description" },
        { key: "link", label: "Source link" },
      ]}
      rows={items.map((s) => {
        const timestamp = s.occurred_at ?? s.observed_at;
        return {
          person: (
            <Inline>
              <Avatar src={s.avatar || ""} name={s.login || "Unknown"} small />
              {s.person_id ? (
                <PersonLink id={s.person_id}>
                  <Text size={TextSize.BODY_SM} weight={TextWeight.MEDIUM} lineClamp={1}>
                    {s.name || s.login}
                  </Text>
                </PersonLink>
              ) : (
                <Text lineClamp={1}>{s.name || s.login || "Unknown account"}</Text>
              )}
            </Inline>
          ),
          signal: (
            <ActivitySignalChip
              activity={s}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                update({
                  activityId: s.id,
                  activityTab: null,
                  personId: null,
                  person: null,
                  personTab: null,
                  companyId: null,
                  companyTab: null,
                  companyPage: null,
                  runId: null,
                  runTab: null,
                });
              }}
            />
          ),
          when: (
            <Text size={TextSize.BODY_SM}>
              <RelativeTime value={timestamp} label={s.occurred_at ? "Occurred" : "Observed"} />
            </Text>
          ),
          activity: s.title || activityVerb(s.type, s.kind, s.baseline),
          source: <SourceLabel source={s.provider} label={sourceLabel(s.provider, s.context)} />,
          observed: date(s.observed_at, true),
          description: (
            <SourceMarkdown content={s.body || activityVerb(s.type, s.kind, s.baseline)} />
          ),
          link: (
            <DlsLink href={safeUrl(s.url) || "#"} isExternal>
              {safeUrl(s.url) || s.url}
            </DlsLink>
          ),
        };
      })}
    />
  );
}

export type FeedPage = {
  total?: number;
  items: ActivityItem[];
  page: number;
  hasNext: boolean;
};
function TimelineFeed({
  initial,
  query = {},
}: {
  initial: FeedPage;
  query?: Record<string, string | undefined>;
}) {
  const [pages, setPages] = useState<FeedPage[]>([]),
    [loading, setLoading] = useState(false);
  const busy = useRef(false),
    abort = useRef<AbortController | null>(null);
  const { toast } = useToast();
  useEffect(() => () => abort.current?.abort(), []);
  const current = pages.at(-1) || initial;
  async function load() {
    if (busy.current) return;
    if (!current.hasNext) return;
    busy.current = true;
    setLoading(true);
    const controller = new AbortController();
    abort.current = controller;
    try {
      const params = new URLSearchParams(
        Object.entries(query).filter((e): e is [string, string] => e[1] !== undefined),
      );
      params.set("page", String(current.page + 1));
      params.set("sort", "when");
      params.set("direction", "desc");
      const page = await unwrap(
        apiClient().GET("/activity", {
          params: { query: Object.fromEntries(params) },
          signal: controller.signal,
        }),
      );
      if (controller.signal.aborted) return;
      setPages((p) => [...p, page]);
    } catch (e) {
      if (!controller.signal.aborted) {
        toast({
          variant: ToastVariant.ERROR,
          header: "Activity unavailable",
          description: (e as Error).message,
          ...toastDefaults(ToastVariant.ERROR),
        });
      }
    } finally {
      busy.current = false;
      setLoading(false);
    }
  }
  return (
    <ActivityTimeline
      items={[initial, ...pages].flatMap((page) => page.items)}
      hasNext={current.hasNext}
      loading={loading}
      onNext={load}
    />
  );
}

export function ActivityFeed(
  props: {
    query?: Record<string, string | undefined>;
    showActions?: boolean;
  } & ({ timeline: true; initial: FeedPage } | { timeline?: false; initial?: FeedPage }),
) {
  return props.timeline ? <TimelineFeed {...props} /> : <PagedActivityFeed {...props} />;
}
function PagedActivityFeed({
  initial,
  query = {},
  showActions = true,
}: {
  initial?: FeedPage;
  query?: Record<string, string | undefined>;
  showActions?: boolean;
}) {
  const { params, update } = useUrlState();
  const sort = useTableSort(activitySortKeys, {
    server: true,
    defaultSort: { sort: "when", direction: "desc" },
  });
  const page = pageNumber(params.get("page") || undefined);
  const client = useQueryClient();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const filters = new URLSearchParams();
  for (const [key, value] of Object.entries(query))
    if (value !== undefined) filters.set(key, value);
  filters.set("page", String(page));
  const signature = filters.toString();
  const result = useQuery<FeedPage>({
    queryKey: ["activity-page", signature],
    initialData: page === pageNumber(query.page) ? initial : undefined,
    queryFn: async ({ signal }) => {
      return unwrap(
        apiClient().GET("/activity", {
          params: { query: Object.fromEntries(new URLSearchParams(signature)) },
          signal,
        }),
      );
    },
    staleTime: 30_000,
    retry: false,
  });
  useEffect(() => {
    function changed(event: Event) {
      const states = (event as CustomEvent<Record<string, { state: string }>>).detail;
      if (states)
        setHidden((previous) => {
          const next = new Set(previous);
          for (const [key, value] of Object.entries(states)) {
            if (
              key.startsWith("person:")
                ? value.state === "processed"
                : query.review && !query.review.split(",").includes(value.state)
            )
              next.add(key);
            else next.delete(key);
          }
          return next;
        });
      void client.invalidateQueries({ queryKey: ["activity-page"] });
    }
    window.addEventListener("records:changed", changed);
    return () => window.removeEventListener("records:changed", changed);
  }, [client, query.review]);
  return (
    <FeedViewport>
      <PeopleFeed
        key={signature}
        selectBatch={
          showActions
            ? () =>
                unwrap(
                  apiClient().GET("/activity/selection", {
                    params: { query: Object.fromEntries(filters) },
                  }),
                )
            : undefined
        }
        sort={sort}
        items={(result.data?.items || []).filter(
          (item) => !hidden.has(item.id) && !hidden.has(`person:${item.person_id}`),
        )}
        showActions={showActions}
        isLoading={result.isFetching || result.isPending}
        error={result.error || undefined}
        onRetry={() => void result.refetch()}
        paging={{
          page,
          total: result.data?.total,
          hasPrevious: page > 1,
          hasNext: page * 40 < (result.data?.total || 0),
          busy: result.isFetching,
          onPrevious: () => update({ page: page > 2 ? String(page - 1) : null }),
          onNext: () => update({ page: String(page + 1) }),
        }}
      />
    </FeedViewport>
  );
}
